import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import cep from 'cep-promise';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { updateMask as updateMaskPhone } from '@/helpers/masks/mobilePhone';
import { updateMask as updateMaskDate } from '@/helpers/masks/generalDate';
import { updateMask as cepMask } from '@/helpers/masks/cep';
import { api } from '@/services/api';
import { getErrorMessage } from '@/helpers/error';
import { setLoading, useLoading } from '@/redux/loading/loadingSlice';
import Client from '@/model/Client';
import { Address } from '@/model/Address';
import { CheckClientDocument } from '@/model/CheckClientDocument';
import { isPendingFixes } from '@/features/core/auth/service/validateClientDataService';
import { FormInputNameFixAddress, FormInputNameFixClientData } from '@/features/profile/types';
import { ErroFields, FixAccountContent } from '@/features/profile/screens/FIX/ui';
import { AuthWrapper } from '@/features/core/auth/screens/AuthWrapper';
import dayjs from 'dayjs';

export const AccountFixScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector(useLoading);
  const history = useNavigate();
  const [checkClientDocument, setCheckClientDocument] = useState<CheckClientDocument>();
  const [checkLoaded, setCheckLoaded] = useState(false);
  const [erroFields, setErroFields] = useState<ErroFields[]>([]);

  const {
    formData: formDataPersonalInfo,
    formErrors: formErrorsPersonalInfo,
    setErrors: setErrorsPersonalInfo,
    onChangeFormInput: onChangeFormInputPersonalInfo,
    isFormValid: isFormValidPersonalInfo,
    resetForm: resetFormPersonalInfo,
  } = useForm({
    initialData: {
      name: '',
      email: '',
      phone: '',
      birthDate: '',
      motherName: '',
    },
    validators: {
      name: [validators.required],
      email: [validators.required, validators.email],
      phone: [validators.required, validators.mobilePhone],
      birthDate: [validators.required, validators.birthday],
      motherName: [validators.required],
    },
    formatters: {
      phone: updateMaskPhone,
      birthDate: updateMaskDate,
    },
  });

  const {
    formData: formDataAddress,
    formErrors: formErrorsAddress,
    setErrors: setErrorsAddress,
    onChangeFormInput: onChangeFormInputAddress,
    isFormValid: isFormValidAddress,
    resetForm: resetFormAddress,
  } = useForm({
    initialData: {
      zipCode: '',
      state: '',
      city: '',
      district: '',
      street: '',
      complement: '',
      number: '',
    },
    validators: {
      zipCode: [validators.required, validators.maxLength(9)],
      state: [validators.required],
      city: [validators.required],
      district: [validators.required],
      street: [validators.required],
      number: [validators.required],
    },
    formatters: {
      zipCode: cepMask,
    },
  });

  const fillFormsFromClient = (data: Client): void => {
    onChangeFormInputPersonalInfo(FormInputNameFixClientData.name)(data.name || '');
    onChangeFormInputPersonalInfo(FormInputNameFixClientData.email)(data.email || '');
    onChangeFormInputPersonalInfo(FormInputNameFixClientData.phone)(data.cellPhone || '');
    onChangeFormInputPersonalInfo(FormInputNameFixClientData.birthDate)(
      data.birthDate ? dayjs(data.birthDate).format('DD/MM/YYYY') : '',
    );
    onChangeFormInputPersonalInfo(FormInputNameFixClientData.motherName)(data.motherName || '');
    if (data.address) {
      const address = data.address as Address;
      onChangeFormInputAddress(FormInputNameFixAddress.id)(address.id);
      onChangeFormInputAddress(FormInputNameFixAddress.zipCode)(address.zipCode || '');
      onChangeFormInputAddress(FormInputNameFixAddress.state)(address.state || '');
      onChangeFormInputAddress(FormInputNameFixAddress.city)(address.city || '');
      onChangeFormInputAddress(FormInputNameFixAddress.district)(address.district || '');
      onChangeFormInputAddress(FormInputNameFixAddress.street)(address.street || '');
      onChangeFormInputAddress(FormInputNameFixAddress.number)(address.number || '');
      onChangeFormInputAddress(FormInputNameFixAddress.complement)(address.complement || '');
    }
  };

  const getCheckOld = async (cpfUser: string): Promise<CheckClientDocument | null> => {
    if (cpfUser && cpfUser.trim().length > 0) {
      const { data } = await api.get<CheckClientDocument>(`/auth/old/${cpfUser}`);
      setCheckClientDocument(data);
      setCheckLoaded(true);
      if (data) {
        if (data.validation) {
          if (data.validation.issues.length > 0) {
            const newErrorFields: ErroFields[] = [];

            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < data.validation.issues.length; i++) {
              const issues = data.validation.issues[i];

              if (issues.field && issues.message) {
                newErrorFields.push({
                  label: issues.field,
                  message: issues.message,
                });
              }
            }

            setErroFields(newErrorFields);
          }
        }
        const hasPersonalPending = isPendingFixes(data);
        const hasAddressPending = !!data.completeAddress;
        if (!hasPersonalPending && !hasAddressPending) {
          history('/');
        }
      }
      return data;
    }
    setCheckLoaded(true);
    return null;
  };

  const getUserData = async (): Promise<void> => {
    const { data } = await api.get<Client>('client/my-account');
    fillFormsFromClient(data);
    getCheckOld(data.cpf);
  };

  const handleOnChangeCEP = async (value: string): Promise<void> => {
    if (value.length === 9) {
      const cepResponse = await cep(value);
      onChangeFormInputAddress(FormInputNameFixAddress.state)(cepResponse.state);
      onChangeFormInputAddress(FormInputNameFixAddress.city)(cepResponse.city);
      onChangeFormInputAddress(FormInputNameFixAddress.district)(cepResponse.neighborhood);
      onChangeFormInputAddress(FormInputNameFixAddress.street)(cepResponse.street);
    }
  };

  const onSubmitPersonalInfo = async (): Promise<boolean> => {
    let ok = false;

    if (isFormValidPersonalInfo()) {
      try {
        const birthDateSplit =
          formDataPersonalInfo[FormInputNameFixClientData.birthDate].split('/');
        const birthDateString = `${birthDateSplit[2]}-${birthDateSplit[1]}-${birthDateSplit[0]}T12:00:00.000Z`;

        const clientPersonalData = {
          name: formDataPersonalInfo[FormInputNameFixClientData.name],
          cellPhone: formDataPersonalInfo[FormInputNameFixClientData.phone],
          email: formDataPersonalInfo[FormInputNameFixClientData.email],
          birthDate: new Date(birthDateString),
          motherName: formDataPersonalInfo[FormInputNameFixClientData.motherName],
        } as Client;

        await api.patch('client/my-account', clientPersonalData);
        ok = true;
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }

    return ok;
  };

  const onSubmitAddress = async (): Promise<boolean> => {
    let ok = false;

    if (isFormValidAddress()) {
      try {
        const address: Address = {
          id: formDataAddress[FormInputNameFixAddress.id],
          zipCode: formDataAddress[FormInputNameFixAddress.zipCode],
          state: formDataAddress[FormInputNameFixAddress.state],
          city: formDataAddress[FormInputNameFixAddress.city],
          district: formDataAddress[FormInputNameFixAddress.district],
          street: formDataAddress[FormInputNameFixAddress.street],
          complement: formDataAddress[FormInputNameFixAddress.complement],
          number: formDataAddress[FormInputNameFixAddress.number],
        };

        await api.patch('client/my-account/address', address);
        ok = true;
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }

    return ok;
  };

  const onSubmitFix = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const pendingFixes = checkClientDocument ? isPendingFixes(checkClientDocument) : true;
      const shouldFixAddress = !!checkClientDocument?.completeAddress;
      const shouldFixPersonal = pendingFixes;
      let savedSomething = false;
      if (shouldFixPersonal) {
        const okPersonal = await onSubmitPersonalInfo();
        if (okPersonal) savedSomething = true;
      }
      if (shouldFixAddress) {
        const okAddr = await onSubmitAddress();
        if (okAddr) savedSomething = true;
      }
      if (savedSomething) toast.success('Dados atualizados com sucesso!');
      await getUserData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const shouldShowAddress = checkLoaded && !!checkClientDocument?.completeAddress;
  const hasPersonalErrors = Object.values(formErrorsPersonalInfo).some(err => err?.length);

  const shouldShowPersonal =
    checkLoaded &&
    ((checkClientDocument && isPendingFixes(checkClientDocument)) || hasPersonalErrors);

  useEffect(() => {
    getUserData();
  }, []);

  return (
    <AuthWrapper isHomepage={false}>
      <FixAccountContent
        state={loading}
        shouldShowPersonal={shouldShowPersonal}
        shouldShowAddress={shouldShowAddress}
        controllerPersonalInfo={{
          formData: formDataPersonalInfo,
          formErrors: formErrorsPersonalInfo,
          setErrors: setErrorsPersonalInfo,
          onChangeFormInput: onChangeFormInputPersonalInfo,
          isFormValid: isFormValidPersonalInfo,
          resetForm: resetFormPersonalInfo,
        }}
        controllerAddress={{
          formData: formDataAddress,
          formErrors: formErrorsAddress,
          setErrors: setErrorsAddress,
          onChangeFormInput: onChangeFormInputAddress,
          isFormValid: isFormValidAddress,
          resetForm: resetFormAddress,
        }}
        onChangeCEP={handleOnChangeCEP}
        onSubmit={onSubmitFix}
        errorFields={erroFields}
      />
    </AuthWrapper>
  );
};
